import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { useApolloClient } from "@apollo/client";
import { GET_MY_SHOPS } from "../graphql/queries";
import { useAuthUser } from "./AuthUserContext";

const SELECTED_SHOP_KEY = "zeqi_selected_shop_id";

const ShopContext = createContext(null);

export const ShopProvider = ({ children }) => {
  const apolloClient = useApolloClient();
  const { user, loading: authLoading } = useAuthUser();
  const authToken = user?.token;

  const [shops, setShops] = useState([]);
  const [selectedShopId, _setSelectedShopId] = useState(
    () => localStorage.getItem(SELECTED_SHOP_KEY) ?? null,
  );
  const [loadingShops, setLoadingShops] = useState(false);

  // Legacy single-shop state kept for backwards compat
  const [shopData, setShopData] = useState(null);

  const setSelectedShopId = useCallback((id) => {
    _setSelectedShopId(id);
    if (id) {
      localStorage.setItem(SELECTED_SHOP_KEY, id);
    } else {
      localStorage.removeItem(SELECTED_SHOP_KEY);
    }
  }, []);

  const refreshShops = useCallback(async () => {
    if (!authToken || authLoading) return;
    setLoadingShops(true);
    try {
      const { data } = await apolloClient.query({
        query: GET_MY_SHOPS,
        fetchPolicy: "network-only",
        context: {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      });
      const fetched = data?.getMyShops ?? [];
      setShops(fetched);

      // Auto-select: restore persisted id if still valid, else pick first
      const persisted = localStorage.getItem(SELECTED_SHOP_KEY);
      const valid = fetched.find((s) => s.id === persisted);
      if (valid) {
        _setSelectedShopId(valid.id);
      } else if (fetched.length > 0) {
        setSelectedShopId(fetched[0].id);
      }
    } catch (err) {
      console.warn("[ShopContext] refreshShops failed:", err);
    } finally {
      setLoadingShops(false);
    }
  }, [apolloClient, authLoading, authToken, setSelectedShopId]);

  const selectedShop =
    shops.find((s) => s.id === selectedShopId) ?? shops[0] ?? null;

  // Keep legacy shopData in sync with selected shop
  useEffect(() => {
    if (selectedShop && !shopData) {
      setShopData(selectedShop);
    }
  }, [selectedShop, shopData]);

  return (
    <ShopContext.Provider
      value={{
        // Multi-shop
        shops,
        selectedShopId,
        selectedShop,
        loadingShops,
        setSelectedShopId,
        refreshShops,
        // Legacy compat
        shopData: shopData ?? selectedShop,
        setShopData,
      }}
    >
      {children}
    </ShopContext.Provider>
  );
};

export const useShop = () => useContext(ShopContext);
